import { useEffect, useState } from 'react';
import { Flex, Box, VStack } from '@chakra-ui/react';
import { Spinner } from 'components/UI';
import ProductView from 'components/common/ProductView';
import Search from 'components/common/Search';
import { matchString } from 'utils/helpers';
import { useDebounce } from 'hooks';
import { IProductResource } from '../../../../server/types';

export type ProductMenuProps = {
  queryParams: Record<string, never>;
  loading?: boolean;
  viewerLoading: boolean;
  products: IProductResource[];
  selectedProduct?: string | number;
  updateSelectedProduct: (
    newSelectedProduct?: number | string,
    currentProducts?: IProductResource[],
    currentUpdateViewer?: boolean | undefined
  ) => void;
};

const ProductMenu = ({
  queryParams = {},
  loading = false,
  viewerLoading = false,
  products,
  selectedProduct,
  updateSelectedProduct,
}: ProductMenuProps) => {
  const [search, setSearch] = useState<string>();
  const [debouncedValue, setDebouncedValue] = useDebounce<string | undefined>(search);

  const updateSelectedProductLocal = (key: string) => {
    if (key) {
      updateSelectedProduct(key);
    }
  };

  const handleClear = () => {
    setSearch('');
    setDebouncedValue('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    if (products && selectedProduct === '' && !queryParams?.product) {
      updateSelectedProductLocal(Object.keys(products)[0]);
    }
  }, [products]);

  if (loading && !products)
    return (
      <Box py={6}>
        <Spinner size="xl" />
      </Box>
    );

  if (!products)
    return (
      <Box>
        <Box textAlign="center">NO PRODUCTS FOUND.</Box>
        <Box p={1} textAlign="center">
          TO ADD A PRODUCT, VISIT THE ASSET MANAGER.
        </Box>
      </Box>
    );

  const renderProducts = Object.entries(products)
    .filter(
      ([, product]) => !debouncedValue || !product.displayName || matchString(product.displayName, debouncedValue)
    )
    .map(([key, product]) => {
      const { name, displayName, thumbnailImage } = product;

      const selected = selectedProduct === key;

      const selectedStyles = selected ? { color: 'base2FontColour.400' } : {};

      return (
        <Flex key={key}>
          <ProductView
            name={name as string}
            productKey={key}
            displayName={displayName as string}
            thumbnail={thumbnailImage as string}
            updateSelectedProduct={updateSelectedProductLocal}
            disabled={viewerLoading}
            buttonProps={{ sx: { w: 'full', ...selectedStyles } }}
            headingProps={
              selected ? { sx: { color: 'base2FontColour.400' } } : { sx: { color: 'base2FontColour.500' } }
            }
          />
        </Flex>
      );
    });

  return (
    <>
      <Box bgColor="base2Colour.400" w="full" pl={4} pr={2} pb={4}>
        <Search
          name="search"
          value={search}
          onChange={handleChange}
          onClear={handleClear}
          placeholder="Filter products by name..."
          containerProps={{
            mt: 3,
          }}
          pr={[14, 16]}
        />
      </Box>
      <VStack
        sx={{
          bgColor: 'base2Colour.500',
          flex: 1,
          minH: 0,
          overflowY: 'scroll',
          w: 'full',
          '&::-webkit-scrollbar': {
            w: 2.5,
          },
          '&::-webkit-scrollbar-thumb': {
            bgColor: 'scrollbarThumbColour.400',
          },
        }}
      >
        {renderProducts}
      </VStack>
    </>
  );
};

export default ProductMenu;
